import {
	type ComponentRendering,
	type HtmlElementRendering,
	type RouteData,
} from "@sitecore-jss/sitecore-jss/layout";
import { type AccordionItemModel } from "~/feature/Content/generated-types";

const accordionComponentName = "feature/Content/Accordion";

type AccordionLayoutRendering = ComponentRendering & {
	customData?: {
		items?: AccordionItemModel[];
	};
};

type JssField<T> = {
	value?: T;
};

const isComponentRendering = (
	rendering: ComponentRendering | HtmlElementRendering
): rendering is ComponentRendering => "componentName" in rendering;

const isFaqAccordion = (rendering: AccordionLayoutRendering) => {
	if (rendering.componentName !== accordionComponentName) {
		return false;
	}

	return (rendering.fields?.isFAQ as JssField<boolean> | undefined)?.value === true;
};

const traverseRenderings = (
	renderings: Array<ComponentRendering | HtmlElementRendering>,
	collect: (rendering: AccordionLayoutRendering) => void
) => {
	for (const rendering of renderings) {
		if (!isComponentRendering(rendering)) {
			continue;
		}

		if (isFaqAccordion(rendering)) {
			collect(rendering);
		}

		if (rendering.placeholders) {
			for (const placeholderName of Object.keys(rendering.placeholders)) {
				traverseRenderings(rendering.placeholders[placeholderName], collect);
			}
		}
	}
};

export type FaqQuestion = {
	name: string;
	text: string;
};

const stripHtmlTags = (value: string) => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const getFaqAccordionQuestions = (route: RouteData): FaqQuestion[] => {
	const questions: FaqQuestion[] = [];

	if (!route.placeholders) {
		return questions;
	}

	for (const placeholderName of Object.keys(route.placeholders)) {
		traverseRenderings(route.placeholders[placeholderName], (rendering) => {
			for (const item of rendering.customData?.items ?? []) {
				if (item.title?.value && item.description?.value) {
					questions.push({
						name: stripHtmlTags(item.title.value),
						text: stripHtmlTags(item.description.value),
					});
				}
			}
		});
	}

	return questions;
};
